import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import { Button } from "@tql/ui-components-react/tql/buttons";
import {
	edit_05,
	file_05,
	lockUnlocked_01,
} from "@tql/ui-components-react/untitled/icons";
import { format, parseISO } from "date-fns";

import type { LoadAccess } from "~/shared/schemas/shipment-management-schema.ts";

import type { DropdownMenuItem } from "./shipment-actions-menu.tsx";
import { ShipmentActionsMenu } from "./shipment-actions-menu.tsx";

// The lock/edit/save/document buttons plus the actions menu. Rendered when the
// shipment is not locked by another user (edit + save), or the force-unlock
// affordance when it is.
const HeaderButtons = ({
	isLockedByOther,
	isStatusLocked,
	isEditing,
	rerateRequired,
	lock,
	onForceUnlock,
	onToggleEditing,
	onSave,
}: {
	isLockedByOther: boolean;
	isStatusLocked: boolean;
	isEditing: boolean;
	rerateRequired: boolean;
	lock: LoadAccess["lock"];
	onForceUnlock: () => void;
	onToggleEditing: () => void;
	onSave: () => void;
}) => {
	// A cancelled or status-locked (e.g. in accounting) shipment cannot be locked, edited, or saved.
	if (isStatusLocked) {
		return null;
	}

	if (isLockedByOther) {
		return (
			<>
				<Button
					aria-label={"Force Unlock Document"}
					data-testid={"force-unlock-trigger"}
					onClick={onForceUnlock}
					variant={"primary"}
				>
					<span className={"edit-button-content"}>
						<SvgIcon icon={lockUnlocked_01} size={"medium"} />
						Force Unlock
					</span>
				</Button>
				{lock ? (
					<span className={"lock-info"}>
						<span className={"lock-info-label"}>
							Locked by {lock.lockedByUserName ?? lock.lockedByUserId}
						</span>{" "}
						{format(parseISO(lock.lockedAt), "MM/dd/yyyy hh:mm a")}
					</span>
				) : null}
			</>
		);
	}

	return (
		<>
			<Button
				aria-label={isEditing ? "Stop Editing Document" : "Edit Document"}
				data-testid={"edit-trigger"}
				disabled={isLockedByOther}
				onClick={onToggleEditing}
				variant={isEditing ? "secondary" : "primary"}
			>
				{isEditing ? (
					"Cancel"
				) : (
					<span className={"edit-button-content"}>
						<SvgIcon icon={edit_05} size={"medium"} />
						Edit
					</span>
				)}
			</Button>
			{isEditing ? (
				<Button
					aria-label={
						rerateRequired ? "Re-rate shipment" : "Save shipment changes"
					}
					data-testid={"save-trigger"}
					onClick={onSave}
					variant={"primary"}
				>
					{rerateRequired ? "Re-rate" : "Save"}
				</Button>
			) : null}
		</>
	);
};

// The shipment management page header: customer/PO title, cancelled and
// status-lock pills, and the lock/edit/save/document action buttons.
export const ShipmentHeader = ({
	customerName,
	poNumber,
	isVoided,
	isQuoteExpired,
	statusLockLabel,
	isLockedByOther,
	isEditing,
	rerateRequired,
	lock,
	dropDownList,
	onForceUnlock,
	onToggleEditing,
	onSave,
	onDocumentClick,
}: {
	customerName: string;
	poNumber: string;
	isVoided: boolean;
	isQuoteExpired: boolean;
	statusLockLabel: string | null;
	isLockedByOther: boolean;
	isEditing: boolean;
	rerateRequired: boolean;
	lock: LoadAccess["lock"];
	dropDownList: DropdownMenuItem[];
	onForceUnlock: () => void;
	onToggleEditing: () => void;
	onSave: () => void;
	onDocumentClick: () => void;
}) => (
	<div className={"flex-header"}>
		<div className={"shipment-title"}>
			<h4 data-testid={"shipment-management-heading"}>
				{customerName}
				{poNumber ? (
					<span className={"shipment-title-po"}>PO{poNumber}</span>
				) : null}
			</h4>
			{isVoided ? <span className={"cancelled-pill"}>Canceled</span> : null}
			{isQuoteExpired ? (
				<span className={"status-lock-pill"} data-testid={"quote-expired-pill"}>
					Quote Expired
				</span>
			) : null}
			{statusLockLabel ? (
				<span className={"status-lock-pill"} data-testid={"status-lock-pill"}>
					{statusLockLabel}
				</span>
			) : null}
		</div>
		<div className={"header-actions"}></div>
		<HeaderButtons
			isEditing={isEditing}
			isLockedByOther={isLockedByOther}
			isStatusLocked={isVoided || statusLockLabel !== null}
			lock={lock}
			onForceUnlock={onForceUnlock}
			onSave={onSave}
			onToggleEditing={onToggleEditing}
			rerateRequired={rerateRequired}
		/>
		<Button
			aria-label={"View shipment documents"}
			data-testid={"document-trigger"}
			onClick={onDocumentClick}
			variant={"tertiary"}
		>
			<SvgIcon icon={file_05} size={"xxlarge"} />
		</Button>
		<ShipmentActionsMenu items={dropDownList} />
	</div>
);
